import { record, pack } from 'rrweb'
import axios from 'axios'
import configObj from '@/config/config'
import { getLocalData, setLocalData } from './utils'
import VueCookie from 'vue-cookie'
import { getVueRoutePathname } from './app-base-path'
const { createEventUploadQueue } = require('./video-record-upload')

const VIDEO_RECORD_REQUEST_TIMEOUT = 10 * 1000

let videoRecordUid = ''

export const videoInstance = {
  events: [],
  mobile: '', // 用户登录的手机号
  status: 0, // 0: 未初始化 1: 录制中 2: 录制结束 -1: 录制异常
  isInit: false,
  pageSceneCode: '',
  startPage: '',
  endPage: '',
  afterEndPage: '',
  pageSceneList: [],
  destroy: () => {},
  timer: null,
  uploader: null,
  flushPromise: null,
  token: '' // 针对部分用户一进来没有token，可以根据上次token来定位是同一个用户
}

const persistVideoRecordEvents = events => {
  localStorage.setItem('video-record-events', JSON.stringify(events))
}

export const setVideoRecordUid = (uid = '') => {
  videoRecordUid = uid || ''
}

const sendVideoRecordBatch = async ({ events, batchId, metadata }) => {
  const res = await axios({
    method: 'post',
    url: configObj.videoRecordUrl,
    data: Object.assign({}, metadata, { events, batchId }),
    timeout: VIDEO_RECORD_REQUEST_TIMEOUT,
    headers: {
      'Content-Type': 'application/json;charset=UTF-8'
    }
  })
  if (res.data.code !== 100000) {
    const error = new Error(res.data.msg || '录屏数据上报失败')
    error.isBusinessError = true
    throw error
  }
}

const getVideoRecordUploader = () => {
  if (!videoInstance.uploader) {
    videoInstance.uploader = createEventUploadQueue({
      events: videoInstance.events,
      persist: persistVideoRecordEvents,
      send: sendVideoRecordBatch,
      onError: error => {
        if (!error.isBusinessError) return
        videoInstance.status = -1
        videoInstance.destroy()
      }
    })
  }
  return videoInstance.uploader
}

const buildVideoRecordMetadata = (mobile, isInit) => ({
  mobile: isInit ? localStorage.getItem('video-record-mobile') : mobile,
  uid: videoRecordUid,
  startTime: localStorage.getItem('video-record-events-start-time'),
  pageSceneCode: localStorage.getItem('video-record-page-scene-code') || videoInstance.pageSceneCode,
  startPage: localStorage.getItem('video-record-start-page') || videoInstance.startPage,
  systemCode: 'unicom',
  projectCode: 'LIANT_YIJIE',
  lastToken: videoInstance.token
})

const parseLocalVideoRecordEvents = () => {
  try {
    const events = JSON.parse(localStorage.getItem('video-record-events') || '[]')
    return Array.isArray(events) ? events : []
  } catch (_) {
    return []
  }
}

/**
 * 初始化视频录制
 * @param {*} mobile 当前用户手机号
 * @param {*} hasReturnData 是否需要先上传一次上一次的录制数据
 * @returns 初始化视频录制失败返回错误信息，上报给problem接口
 */
export const handleInitVideoRecord = async (mobile, hasReturnData) => {
  if (process.env.NODE_ENV === 'development') return false
  if (!mobile) return '手机号不存在 || 用户未登录'
  videoInstance.status = 0
  videoInstance.mobile = mobile
  videoInstance.token = localStorage.getItem('last-token')
  const isUploaded = await handleUploadVideoRecord(mobile, true)
  if (!isUploaded) return '上次录屏数据上传失败'
  if (hasReturnData && isUploaded) handleRemoveLocalStorage()
  videoInstance.pageSceneList = await handleGetPageSceneList()
  if (!handleMatchStartPageScene(getVueRoutePathname(location.pathname))) return { error: '此场景不需要录制', destroy: () => {} }
  const { error } = await handleStartVideoRecord()
  if (error) return error
  return false
}

/**
 * 初始化本地存储
 * @param {*} mobileKey 上一次的用户手机号
 * @param {*} timeKey 上一次的录制开始时间
 * @param {*} eventsKey 上一次的录制事件
 * @param {*} mobile 当前的用户手机号
 */
const handleInitLocalStorage = (mobileKey, timeKey, eventsKey, mobile) => {
  if (!getLocalData(mobileKey)) setLocalData(mobileKey, mobile)
  const logStartTime = getLocalData('log-start-time')
  if (!getLocalData(timeKey)) setLocalData(timeKey, logStartTime)
  if (!getLocalData(eventsKey)) setLocalData(eventsKey, JSON.stringify([]))
}

/**
 * 开始录制视频
 * @returns 开始录制视频失败返回错误信息
 */
export const handleStartVideoRecord = async () => {
  let err = ''
  let destroy = () => {}
  try {
    if (videoInstance.flushPromise) {
      const isFlushed = await videoInstance.flushPromise
      videoInstance.flushPromise = null
      if (!isFlushed) {
        videoInstance.status = -1
        return { err: '上一段录屏数据上传失败', destroy }
      }
    }
    if (videoInstance.status === 1) {
      videoInstance.destroy()
      const isFlushed = await getVideoRecordUploader().drain(buildVideoRecordMetadata(videoInstance.mobile, false))
      if (!isFlushed) {
        videoInstance.status = -1
        return { err: '当前录屏数据上传失败', destroy }
      }
    }
    destroy = record({
      emit (event) {
        getVideoRecordUploader().append(event)
      },
      packFn: pack,
      checkoutEveryNms: 10 * 60 * 1000, // 全量快照时间间隔
      sampling: {
        mousemove: false, // 不录制鼠标移动事件
        mouseInteraction: true, // 不录制鼠标交互事件
        scroll: 150, // 每 150ms 最多触发一次滚动交互
        media: false, // 关闭媒体录制
        input: 'last' // 连续输入时只录制最终值
      }
    })
    videoInstance.status = 1
    videoInstance.timer = handleCountTime(0, videoInstance.mobile)
    handleInitLocalStorage('video-record-mobile', 'video-record-events-start-time', 'video-record-events', videoInstance.mobile)
    videoInstance.destroy = destroy
  } catch (error) {
    videoInstance.status = -1
    err = error
  }
  return { err, destroy }
}

/**
 * 上传视频录制数据
 * @param {*} mobile 当前用户手机号
 * @param {*} hasReturnData 是否需要先上传一次上一次的录制数据
 */
export const handleUploadVideoRecord = (mobile, isInit) => {
  if (videoInstance.status === -1) return Promise.resolve(false)
  const metadata = buildVideoRecordMetadata(mobile, isInit)
  if (!isInit) return getVideoRecordUploader().upload(metadata)

  const localEvents = parseLocalVideoRecordEvents()
  const localUploader = createEventUploadQueue({
    events: localEvents,
    persist: persistVideoRecordEvents,
    send: sendVideoRecordBatch
  })
  return localUploader.upload(metadata)
}

/**
 * 间隔10s计时上传视频录制数据
 * @param {*} time 计时时间
 * @param {*} mobile 当前用户手机号
 */
export const handleCountTime = (time, mobile) => {
  if (videoInstance.status === -1) return
  const timer = setTimeout(async () => {
    time++
    if (time >= 5) {
      await handleUploadVideoRecord(mobile)
      time = 0
    }
    if (videoInstance.status !== -1) {
      videoInstance.timer = handleCountTime(time, mobile)
    }
  }, 1000)
  return timer
}

// 获取页面场景列表
export const handleGetPageSceneList = () => {
  return axios.post(configObj.videoRecordPageSceneListUrl, { systemCode: 'unicom', projectCode: 'LIANT_YIJIE' }).then(res => {
    if (res.data.code !== 100000) {
      videoInstance.status = -1
    } else {
      return res.data.data
    }
  })
}

// 获取页面场景代码
export const handleSetPageSceneCode = (code, startPage, endPage, afterEndPage) => {
  videoInstance.pageSceneCode = code
  videoInstance.startPage = startPage
  videoInstance.endPage = endPage
  videoInstance.afterEndPage = afterEndPage
  setLocalData('video-record-page-scene-code', code)
  setLocalData('video-record-start-page', startPage)
  setLocalData('video-record-end-page', endPage)
  setLocalData('video-record-after-end-page', afterEndPage)
}

// 匹配开始页面场景
export const handleMatchStartPageScene = (pathname, fromPathname) => {
  let isMatch = false
  const pageScene = videoInstance.pageSceneList.find(item => item.startPage === pathname)
  if (pageScene) {
    handleSetPageSceneCode(pageScene.pageSceneCode, pageScene.startPage, pageScene.endPage, pageScene.afterEndPage)
    isMatch = true
  }
  if (fromPathname === '/' || videoInstance.status === 1) return false // 阻止第一次进入页面时录制
  if (localStorage.getItem('video-record-events-start-time') && videoInstance.status === 0) return true // 如果已经录制过，则需要录制，防止用户半路退出
  return isMatch
}

// 匹配结束页面场景
export const handleMatchEndPageScene = (fromPathname, toPathname) => {
  return fromPathname === videoInstance.endPage && toPathname === videoInstance.afterEndPage
}

// 初始化视频录制
export const handleResetVideoRecordInstance = () => {
  clearTimeout(videoInstance.timer)
  videoInstance.destroy()
  videoInstance.status = 0
  const uploadPromise = getVideoRecordUploader().drain(buildVideoRecordMetadata(videoInstance.mobile, false))
  videoInstance.flushPromise = uploadPromise
  videoInstance.pageSceneCode = ''
  videoInstance.startPage = ''
  videoInstance.endPage = ''
  videoInstance.pageSceneList = []
  videoInstance.destroy = () => {}
  if (uploadPromise) {
    uploadPromise.then(isUploaded => {
      if (isUploaded && !videoInstance.events.length) {
        localStorage.removeItem('video-record-events')
      }
      if (!isUploaded) videoInstance.status = -1
    })
  }
}

const handleRemoveLocalStorage = () => {
  const arr = ['video-record-events', 'video-record-events-start-time', 'video-record-mobile', 'video-record-page-scene-code', 'video-record-start-page', 'video-record-end-page', 'video-record-after-end-page']
  arr.forEach(item => {
    localStorage.removeItem(item)
    sessionStorage.removeItem(item)
    VueCookie.delete(item)
  })
}
